import { useEffect, useMemo, useRef, useState } from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import { grey } from "@mui/material/colors";

import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { isEmpty } from "lodash";

import { AgGridReact } from "ag-grid-react";

import fpValidationService from "@plannel/services/fp/fp-validation-service";
import fpCommonService from "@plannel/services/fp/fp-common-service";
import PlanScope from "@plannel/pages/factory-plan/common/component/PlanScope";
import SearchOptsGroup from "@plannel/pages/factory-plan/common/component/SearchOptsGroup";
import ValidationAccordion from "@plannel/pages/factory-plan/master/validation/ValidationAccordion";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import Snackbar from "@plannel/components/Snackbar";
import FpGridSetting from "@plannel/pages/factory-plan/common/grid";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import reduxUtil from "@plannel/utils/redux-util";
import { useMenuAccess } from "@plannel/hooks/useMenuAccess";
import dateUtils from "@plannel/utils/date-util";
import debug from "@plannel/utils/debug-util";
import ColorUtil from "@plannel/pages/utils/color-util";

const { snackBarMessageColor } = ColorUtil;

const VALID_TYPE_GRP_CD = "FP_VALID_TYPE";

// 이 화면의 고정 조회조건(§10) — planScope(서버) 외 검증유형 select 하나뿐, 버튼 그룹은
// FilterContainer 마지막 자식(§6)에 직접 배치.
const SEARCH_FIELD_LABELS = { validationType: "FP_VALIDATION_TYPE" };
const SEARCH_FIELD_KEYS = Object.keys(SEARCH_FIELD_LABELS);

// old modelMenuPath 대응 — 모델 유형별 이동 대상 화면(reduxKey)과 그 화면에 채울 조회조건 필드.
// tabIndex가 있으면 대상 화면의 탭도 함께 전환한다(Item=품목/인벤토리, Order=판매주문/작업주문).
const MODEL_NAV_MAP = {
  ROUTE: { reduxKey: "FP_BOR", field: "routeCdNm" },
  ITEM: { reduxKey: "FP_ITEM", field: "itemCdNm", tabIndex: 0 },
  INVENTORY: { reduxKey: "FP_ITEM", field: "inventoryCdNm", tabIndex: 1 },
  WORK_ORDER: { reduxKey: "FP_ORDER", field: "woCdNm", tabIndex: 1 },
  DEMAND: { reduxKey: "FP_ORDER", field: "dmndCdNm", tabIndex: 0 },
};

const Validation = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();
  const { isPermitted } = useMenuAccess();

  const [planScope, setPlanScope] = useState(reduxViewState?.planScope ?? "");
  const [searchFields, setSearchFields] = useState({
    validationType: reduxViewState?.validationType ?? "FP_VALID_TYPE_ALL",
  });
  const [validationTypeOptions, setValidationTypeOptions] = useState([]);
  const [validationResult, setValidationResult] = useState([]);
  const [modelRows, setModelRows] = useState([]);
  // 우측 그리드 위 섹션 라벨 — { main: 검증유형, detail: "세부항목 ( 건수 )" }(§15-1a).
  const [modelGridTitle, setModelGridTitle] = useState(null);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  const gridRef = useRef();
  // planScope를 빠르게 바꾸면 이전 요청 응답이 늦게 도착해 최신 결과를 덮어쓰는 걸 막는 요청 토큰 가드(§6).
  const latestReqRef = useRef(0);
  // onCellClicked 핸들러(아래)는 onGridReady 시점에 한 번 캡처되므로, planScope를 그대로 클로저에
  // 담으면 그 이후 바뀐 값을 못 본다 — 항상 최신값을 읽도록 ref로 따로 보관.
  const planScopeRef = useRef(planScope);
  useEffect(() => {
    planScopeRef.current = planScope;
  }, [planScope]);

  const defaultGridMemo = useMemo(() => ({
    ...FpGridSetting({ title, viewName }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), []);

  // old modelMenuPath+history.push 대응 — MODEL_NAV_MAP으로 대상 화면(reduxKey)/필드를 찾아
  // 그 화면의 viewState에 조회조건을 미리 심어두고(updateViewState), historyState로 탭 전환을
  // 요청한다(updateHistoryState). 대상 화면은 자신의 viewName과 historyState가 일치할 때
  // useSelector(state => state.historyState.history)로 이 신호를 감지해 viewState를 다시 읽는다.
  //   대상 화면이 이 테넌트/사용자 권한 밖이면 링크로 취급하지 않는다 — 밑줄·포인터도 빼서 눌러볼
  //   여지를 없앤다(cellStyle 과 클릭 핸들러가 같은 판정을 쓰도록 한 곳으로 묶었다).
  const navFor = (model) => {
    const nav = MODEL_NAV_MAP[model];
    return nav && isPermitted(nav.reduxKey) ? nav : null;
  };

  const onModelCdClicked = (params) => {
    const nav = navFor(params.data?.model);
    if (!nav) return;

    const navFields = { planScope: planScopeRef.current, [nav.field]: params.data.modelCd };
    if (nav.tabIndex !== undefined) navFields.tabIndex = nav.tabIndex;

    reduxDispatch(reduxUtil.updateViewState(reduxUtil.createObjectState(nav.reduxKey, navFields)));
    reduxDispatch(reduxUtil.updateHistoryState(nav.reduxKey));
  };

  const modelGridColumnDefs = [
    { headerName: "FP_MODEL", field: "model", colId: "model", type: ["nonEditableColumn"], width: 150 },
    { headerName: "FP_MODEL_CD", field: "modelCd", colId: "modelCd", type: ["nonEditableColumn"], width: 150,
      cellStyle: params => (navFor(params.data?.model) ? { cursor: "pointer", textDecoration: "underline" } : null),
      onCellClicked: onModelCdClicked },
    { headerName: "FP_MODEL_NM", field: "modelNm", colId: "modelNm", type: ["nonEditableColumn"], width: 200 },
    {
      headerName: "FP_COL_AUDIT", groupId: "audit", marryChildren: true, openByDefault: false,
      children: [
        { headerName: "updatedTs", field: "modifyDttm", colId: "modifyDttm", type: ["nonEditableColumn", "centerAligned"], width: 150, valueFormatter: dateUtils.formatDateTime },
        { headerName: "updatedBy", field: "modifyBy", colId: "modifyBy", type: ["nonEditableColumn", "centerAligned"], width: 110, columnGroupShow: "open" },
        { headerName: "createdTs", field: "createDttm", colId: "createDttm", type: ["nonEditableColumn", "centerAligned"], width: 150, valueFormatter: dateUtils.formatDateTime, columnGroupShow: "open" },
        { headerName: "createdBy", field: "createBy", colId: "createBy", type: ["nonEditableColumn", "centerAligned"], width: 110, columnGroupShow: "open" },
      ],
    },
  ];

  useEffect(() => {
    fpCommonService.getCodes(VALID_TYPE_GRP_CD)
      .then((response) => {
        const options = (response.data ?? [])
          .slice()
          .sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0))
          .map((code) => ({ value: code.comnCd, label: t(code.comnCd) }));
        setValidationTypeOptions(options);
      })
      .catch((e) => debug(e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runValidation = () => {
    if (!planScope) return;

    const reqId = ++latestReqRef.current;
    // 검증 실행 직후엔 아직 고른 항목이 없다 — 제목만 있고 부가정보(detail)는 없는 상태(§15-1a).
    setModelGridTitle({ main: t("VALIDATION_LIST") });
    setModelRows([]);
    gridRef.current?.api?.setGridOption("loading", true);

    fpValidationService.getAll({ planScope, type: searchFields.validationType })
      .then((response) => {
        if (reqId !== latestReqRef.current) return;
        const results = response.data;
        if (isEmpty(results)) {
          setValidationResult([]);
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        } else {
          setValidationResult(results);
        }
      })
      .catch((e) => {
        debug(e);
      })
      .then(() => {
        if (reqId !== latestReqRef.current) return;
        gridRef.current?.api?.setGridOption("loading", false);
      });
  };

  // 서버 조회는 planScope 또는 검증유형 변경 시(§6 — PlanScope가 최초 목록 로드 후 자동으로 값을
  // 채워줌). old는 명시적 조회 버튼이 있었으나, new는 버튼 없이 콤보 선택만으로 즉시 재조회한다.
  useEffect(() => {
    runValidation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planScope, searchFields.validationType]);

  const onSearchFieldChange = (tpCd, value) => setSearchFields((prev) => ({ ...prev, [tpCd]: value }));

  const onSelectDetail = (models, title) => {
    setModelGridTitle(title);
    setModelRows(models ?? []);
  };

  // 화면 뷰 상태 영속화(planScope/validationType) — 탭 전환 시 유지(CLAUDE.md redux-util 관례).
  useEffect(() => {
    const reduxState = reduxUtil.createObjectState(viewName, { planScope, ...searchFields });
    reduxDispatch(reduxUtil.updateViewState(reduxState));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planScope, searchFields]);

  const onGridReady = (params) => {
    GridUtils.setColumnDefs({ ...params, columnDefs: modelGridColumnDefs, viewName, initState: true });
  };

  return (
    <>
      <FilterContainer>
        <PlanScope value={planScope} onChange={setPlanScope} />
        <SearchOptsGroup
          fields={searchFields}
          fieldKeys={SEARCH_FIELD_KEYS}
          onChange={onSearchFieldChange}
          labels={SEARCH_FIELD_LABELS}
          fieldTypes={{ validationType: "select" }}
          selectOptions={{ validationType: validationTypeOptions }}
        />
        {/* 버튼 그룹이 FilterContainer 밖(좌측 Accordion 영역 상단)에 있는 화면 — 마지막 자식을
            빈 Box로 둬서 SearchOptsGroup이 float:right로 PlanScope와 멀어지는 걸 막는다(§6). */}
        <Box />
      </FilterContainer>
      <Box sx={{ display: "flex", flex: 1, minHeight: 0, gap: 1, mt: 1 }}>
        <Box sx={{ width: "25%", display: "flex", flexDirection: "column" }}>
          {/* 좌/우 헤더줄 높이를 맞춰 아코디언과 그리드 상단이 어긋나지 않게 한다(§15-1a). */}
          <Box sx={{ display: "flex", alignItems: "center", mb: 1, minHeight: 40 }}>
            <Button size="small" onClick={runValidation}>
              {t("FP_RUN_VALIDATION")}
            </Button>
          </Box>
          <Box sx={{ overflow: "auto" }}>
            <ValidationAccordion data={validationResult} onSelect={onSelectDetail} />
          </Box>
        </Box>
        <Box sx={{ width: "75%", display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
          {/* 선택한 검증 항목 라벨 — 제목(굵기 500) + 구분선 + 세부항목/건수(보조색), §15-1a. */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1, minHeight: 40, minWidth: 0 }}>
            {modelGridTitle?.main && (
              <Box component="span" sx={{ fontWeight: 500, whiteSpace: "nowrap" }}>{modelGridTitle.main}</Box>
            )}
            {/* 구분선은 **부가정보가 실제로 있을 때만** — 항목을 고르기 전에는 선만 남는다. */}
            {modelGridTitle?.detail && (
              <>
                <Divider orientation="vertical" sx={{ height: 16, alignSelf: "center", borderColor: grey[400] }} />
                <Box
                  component="span"
                  sx={{ color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                >
                  {modelGridTitle.detail}
                </Box>
              </>
            )}
          </Box>
          <Box id="fp-validation-model-grid" className="ag-theme-balham grid-area" sx={{ flex: 1, minHeight: 0, pb: 1 }}>
            <AgGridReact
              ref={gridRef}
              rowData={modelRows}
              getRowId={(params) => `${params.data.model}_${params.data.modelCd}`}
              {...defaultGridMemo}
              onGridReady={onGridReady}
            />
          </Box>
        </Box>
      </Box>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(Validation);
